/**
 * Importar credenciales desde CSV pegado (F3c). Es el camino para migrar el
 * sheet de contraseñas al Vault y darlo de baja (doc §2.2, fase 3).
 *
 * Columnas (cabecera obligatoria, orden libre; separador , ; o tab):
 *   name, username, password, url?, area?, clientId?, ownerEmail?, notes?, totpSeed?, tags?
 * La cabecera acepta los nombres habituales de una planilla en español ("Plataforma",
 * "Usuario", "Contraseña", "Link"…), y el área y el owner se pueden fijar para todas las
 * filas desde el diálogo (14/10/2026: Sol migra un Sheet de Paid Media pestaña por pestaña
 * sin tocar la planilla). Se parsea en el cliente para mostrar el preview. El server valida POR FILA
 * (una fila mala no aborta la tanda) y omite lo que ya existe (reintentar tras
 * un corte no duplica). Se manda en tandas de BATCH filas encadenadas: el
 * Router corta a los 30 s (review F3c).
 */
import { useMemo, useState } from 'react';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { trpc } from '@/lib/trpc';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { errMsg, withUnlockRetry } from '@/hooks/useVault';
import type { RouterOutputs } from '@/lib/api-types';
import { VAULT_AREAS } from '../../../../shared/types/vault';
import { normalizeEmail } from '../../../../shared/identity';

const COLUMNS = ['name', 'username', 'password', 'url', 'area', 'clientId', 'ownerEmail', 'notes', 'totpSeed', 'tags'] as const;
type Col = (typeof COLUMNS)[number];
type Row = Partial<Record<Col, string>>;
type RowResult = RouterOutputs['vault']['importSecrets']['results'][number];

/** Mismo tope que IMPORT_BATCH_MAX en el router. */
const BATCH = 25;
// Radix Select no acepta value="": centinela para "ninguna" (misma convención que los otros diálogos del Vault).
const NONE = '__none__';
const MAX_ROWS = 500;

/** Split de una línea CSV respetando comillas dobles ("" = comilla literal). */
function splitWith(line: string, sep: string): string[] {
  const out: string[] = [];
  let cur = '';
  let inQ = false;
  for (let i = 0; i < line.length; i++) {
    const c = line[i];
    if (inQ) {
      if (c === '"' && line[i + 1] === '"') { cur += '"'; i++; }
      else if (c === '"') inQ = false;
      else cur += c;
    } else if (c === '"') inQ = true;
    else if (c === sep) { out.push(cur); cur = ''; }
    else cur += c;
  }
  out.push(cur);
  return out.map((v) => v.trim());
}

const HEADER_ALIASES: Array<[Col, RegExp]> = [
  ['name', /^(nombre|cuenta|credencial|plataforma|servicio|herramienta|tool|platform|account)$/i],
  ['username', /^(usuario|user|email|e-mail|mail|correo|login|usr)$/i],
  ['password', /^(contraseña|contrasena|pass|clave|password|pwd|pw)$/i],
  ['url', /^(link|web|sitio|site|sitio web|website|enlace)$/i],
  ['ownerEmail', /^(owner|dueño|dueno|responsable)$/i],
  ['notes', /^(notas|observaciones|comentarios|nota|obs)$/i],
  ['area', /^(área|equipo|team)$/i],
  ['clientId', /^(cliente|client)$/i],
];

/** CSV mínimo; detecta el separador por la cabecera (respetando comillas). */
/** No es el `parseCsv` RFC 4180 de shared/ta-metrics.ts: este detecta separador y mapea cabeceras (deuda: unificar en shared/csv.ts). */
function parseVaultCsv(text: string, defaults: { area?: string; ownerEmail?: string } = {}): { rows: Row[]; missing: string[]; unknownAreas: string[] } {
  const lines = text.replace(/^\uFEFF/, '').replace(/\r\n?/g, '\n').split('\n').filter((l) => l.trim());
  if (lines.length < 2) return { rows: [], missing: [], unknownAreas: [] };
  const sep = [',', ';', '\t'].map((s) => ({ s, n: splitWith(lines[0], s).length })).sort((a, b) => b.n - a.n)[0].s;
  const header = splitWith(lines[0], sep);
  const idx = new Map<Col, number>();
  header.forEach((h, i) => {
    const key = COLUMNS.find((c) => c.toLowerCase() === h.toLowerCase()) ?? HEADER_ALIASES.find(([, re]) => re.test(h))?.[0];
    if (key && !idx.has(key)) idx.set(key, i);
  });
  const missing = (['name', 'username', 'password'] as const).filter((req) => !idx.has(req));
  if (missing.length) return { rows: [], missing, unknownAreas: [] };
  const rows: Row[] = lines.slice(1).map((line) => {
    const cells = splitWith(line, sep);
    const row: Row = {};
    for (const [col, i] of idx) if (cells[i]) row[col] = cells[i];
    // Área / owner para todas las filas que no lo traigan en la planilla.
    if (!row.area && defaults.area) row.area = defaults.area;
    if (!row.ownerEmail && defaults.ownerEmail) row.ownerEmail = defaults.ownerEmail;
    return row;
  });
  const knownAreas = new Set<string>(VAULT_AREAS.map((a) => a.id));
  const unknownAreas = [...new Set(rows.map((r) => r.area?.trim().toLowerCase()).filter((a): a is string => !!a && !knownAreas.has(a)))];
  return { rows, missing, unknownAreas };
}

export function ImportDialog({ open, onOpenChange, ensureUnlocked }: { open: boolean; onOpenChange: (o: boolean) => void; ensureUnlocked: () => Promise<boolean> }) {
  const { t } = useTranslation();
  const utils = trpc.useUtils();
  const [text, setText] = useState('');
  const [defaultArea, setDefaultArea] = useState('');
  const [defaultOwner, setDefaultOwner] = useState('');
  const [result, setResult] = useState<RowResult[] | null>(null);
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null);
  const parsed = useMemo(
    () => parseVaultCsv(text, { area: defaultArea || undefined, ownerEmail: normalizeEmail(defaultOwner) || undefined }),
    [text, defaultArea, defaultOwner]
  );
  const importRows = trpc.vault.importSecrets.useMutation();
  const busy = progress !== null;

  const run = async () => {
    const rows = parsed.rows.slice(0, MAX_ROWS);
    const all: RowResult[] = [];
    setProgress({ done: 0, total: rows.length });
    try {
      for (let i = 0; i < rows.length; i += BATCH) {
        const chunk = rows.slice(i, i + BATCH);
        // importar crea credenciales: exige el Vault desbloqueado, como Nueva credencial (auditoría 28/09)
        const r = await withUnlockRetry(ensureUnlocked, () => importRows.mutateAsync({ rows: chunk }));
        if (!r) break;
        all.push(...r.results.map((x) => ({ ...x, row: x.row + i })));
        setProgress({ done: Math.min(i + BATCH, rows.length), total: rows.length });
      }
    } catch (e) {
      // Una tanda cortada: se muestra lo que sí entró; reintentar no duplica.
      toast.error(errMsg(e as { message?: string }));
    } finally {
      setProgress(null);
    }
    if (all.length) {
      setResult(all);
      setText(''); // las contraseñas no se quedan en el DOM
      utils.vault.listSecrets.invalidate();
      const ok = all.filter((x) => x.ok && !x.skipped).length;
      const skipped = all.filter((x) => x.skipped).length;
      toast[all.every((x) => x.ok) ? 'success' : 'warning'](
        t('vault.import.done', '{{ok}} de {{total}} credenciales importadas', { ok, total: all.length }) +
          (skipped ? ` · ${t('vault.import.skipped_n', '{{n}} ya existían', { n: skipped })}` : '')
      );
    }
  };

  const close = (o: boolean) => {
    if (busy) return;
    if (!o) { setText(''); setResult(null); setDefaultArea(''); setDefaultOwner(''); }
    onOpenChange(o);
  };

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-3xl">
        <DialogHeader>
          <DialogTitle>{t('vault.import.title', 'Importar credenciales desde CSV')}</DialogTitle>
          <DialogDescription>
            {t('vault.import.sub', 'Pegá el CSV con cabecera: name, username, password y opcionales url, area, clientId, ownerEmail, notes, totpSeed, tags. Cada fila se cifra al entrar; el texto pegado no se guarda en ningún lado.')}
          </DialogDescription>
        </DialogHeader>

        {result ? (
          <div className="space-y-2">
            <ul className="max-h-80 divide-y overflow-y-auto rounded-md border text-sm">
              {result.map((r) => (
                <li key={r.row} className="flex items-center justify-between gap-3 px-3 py-1.5">
                  <span className="truncate">#{r.row} · {r.name}</span>
                  <span className={r.ok ? (r.skipped ? 'text-muted-foreground' : 'text-emerald-600') : 'text-rose-600'}>
                    {r.ok ? (r.skipped ? t('vault.import.skipped', 'ya existía (omitida)') : t('vault.import.ok', 'OK')) : r.error}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="space-y-1">
                <Label className="text-xs">{t('vault.import.default_area', 'Área para todas las filas (si la planilla no la trae)')}</Label>
                <Select value={defaultArea || NONE} onValueChange={(v) => setDefaultArea(v === NONE ? '' : v)} disabled={busy}>
                  <SelectTrigger className="h-9 text-sm"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value={NONE}>{t('vault.import.default_area_none', 'Sin área (la que diga cada fila)')}</SelectItem>
                    {VAULT_AREAS.map((a) => <SelectItem key={a.id} value={a.id}>{a.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label className="text-xs">{t('vault.import.default_owner', 'Owner para todas las filas (vacío = vos)')}</Label>
                <Input value={defaultOwner} onChange={(e) => setDefaultOwner(e.target.value)} placeholder="nombre@winclap.com" className="h-9 text-sm" autoComplete="off" disabled={busy} />
              </div>
            </div>
            <Textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              rows={8}
              className="font-mono text-xs"
              placeholder={t('vault.import.placeholder', 'name,username,password,url,area,ownerEmail\nMeta Business — LATAM,accounts@winclap.com,•••••,https://business.facebook.com,paidmedia,sofia@winclap.com')}
              autoComplete="off"
              spellCheck={false}
              disabled={busy}
            />
            {parsed.missing.map((col) => <p key={col} className="text-sm text-rose-600">{t('vault.import.missing_col', 'Falta la columna "{{col}}"', { col })}</p>)}
            {parsed.unknownAreas.length > 0 && (
              <p className="text-sm text-amber-700">
                {t('vault.import.unknown_areas', 'Áreas que no existen en el Vault (esas filas van a fallar): {{areas}}. Válidas: {{valid}}.', { areas: parsed.unknownAreas.join(', '), valid: VAULT_AREAS.map((a) => a.id).join(', ') })}
              </p>
            )}
            {parsed.rows.length > MAX_ROWS && (
              <p className="text-sm text-amber-700">
                {t('vault.import.truncated', 'Se van a importar solo las primeras {{max}} de {{n}} filas. Dividí el archivo para el resto.', { max: MAX_ROWS, n: parsed.rows.length })}
              </p>
            )}
            {parsed.rows.length > 0 && (
              <div className="rounded-md border p-3 text-sm">
                <div className="mb-1 font-medium">{t('vault.import.preview', '{{n}} filas detectadas', { n: parsed.rows.length })}</div>
                <ul className="max-h-40 space-y-0.5 overflow-y-auto text-xs text-muted-foreground">
                  {parsed.rows.slice(0, 50).map((r, i) => (
                    <li key={i} className="truncate">{r.name} · {r.username}{r.area ? ` · ${r.area}` : ''}{r.ownerEmail ? ` · ${t('vault.import.owner_of', 'owner {{email}}', { email: r.ownerEmail })}` : ''}{r.password ? '' : ` · ${t('vault.import.no_password', 'SIN CONTRASEÑA')}`}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => close(false)} disabled={busy}>{result ? t('vault.import.close', 'Cerrar') : t('common.ui.cancel', 'Cancelar')}</Button>
          {!result && (
            <Button onClick={run} disabled={busy || parsed.rows.length === 0 || parsed.missing.length > 0}>
              {progress
                ? t('vault.import.progress', 'Importando {{done}}/{{total}}…', { done: progress.done, total: progress.total })
                : t('vault.import.cta', 'Importar {{n}}', { n: Math.min(parsed.rows.length, MAX_ROWS) })}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
